import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  Schedule as ScheduleIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { emailNotificationApi } from '../../api/emailNotificationApi';

const ScheduledNotificationsPage = () => {
  const navigate = useNavigate();
  const accessToken = localStorage.getItem('accessToken');

  // États
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    pending: 0,
    sent: 0,
    failed: 0,
    total: 0
  });
  const [history, setHistory] = useState([]);
  const [failedNotifications, setFailedNotifications] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTemplate, setFilterTemplate] = useState('');
  const [templates, setTemplates] = useState([]);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [showFailedDialog, setShowFailedDialog] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showNotificationDialog, setShowNotificationDialog] = useState(false);

  // Charger les données initiales
  useEffect(() => {
    loadStats();
    loadTemplates();
  }, []);

  // Charger les statistiques
  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await emailNotificationApi.getNotificationStats(accessToken);
      if (response.status === 200) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Charger les templates
  const loadTemplates = async () => {
    try {
      const response = await emailNotificationApi.getAllTemplates(accessToken);
      if (response.status === 200) {
        setTemplates(response.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des templates:', error);
    }
  };

  // Générer les notifications programmées
  const handleGenerateNotifications = async () => {
    try {
      setLoading(true);
      const response = await emailNotificationApi.generateScheduledNotifications(accessToken);
      
      if (response.status === 200) {
        alert(`Génération terminée: ${response.data.generated} notifications créées`);
        loadStats();
      } else {
        alert('Erreur lors de la génération: ' + response.message);
      }
    } catch (error) {
      console.error('Erreur lors de la génération:', error);
      alert('Erreur lors de la génération des notifications');
    } finally {
      setLoading(false);
    }
  };

  // Traiter les notifications programmées
  const handleProcessNotifications = async () => {
    try {
      setLoading(true);
      const response = await emailNotificationApi.processScheduledNotifications(accessToken);
      
      if (response.status === 200) {
        const { processed, success, failed } = response.data;
        alert(`Traitement terminé: ${processed} traitées, ${success} succès, ${failed} échecs`);
        loadStats();
      } else {
        alert('Erreur lors du traitement: ' + response.message);
      }
    } catch (error) {
      console.error('Erreur lors du traitement:', error);
      alert('Erreur lors du traitement des notifications');
    } finally {
      setLoading(false);
    }
  };

  // Charger l'historique
  const loadHistory = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        limit: 20,
        offset: (page - 1) * 20,
        status: filterStatus || undefined,
        templateId: filterTemplate || undefined
      };
      
      const response = await emailNotificationApi.getNotificationHistory(params, accessToken);
      
      if (response.status === 200) {
        setHistory(response.data);
        setTotalPages(Math.ceil(response.data.length / 20));
      }
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique:', error);
    } finally {
      setLoading(false);
    }
  };

  // Charger les notifications échouées
  const loadFailedNotifications = async () => {
    try {
      setLoading(true);
      const response = await emailNotificationApi.getFailedNotificationsForRetry(accessToken);
      
      if (response.status === 200) {
        setFailedNotifications(response.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des notifications échouées:', error);
    } finally {
      setLoading(false);
    }
  };

  // Retenter une notification
  const handleRetryNotification = async (id) => {
    try {
      setLoading(true);
      const response = await emailNotificationApi.retryNotification(id, accessToken);
      
      if (response.status === 200) {
        alert('Notification retentée avec succès');
        loadFailedNotifications();
        loadStats();
      } else {
        alert('Erreur lors du retry: ' + response.message);
      }
    } catch (error) {
      console.error('Erreur lors du retry:', error);
      alert('Erreur lors du retry de la notification');
    } finally {
      setLoading(false);
    }
  };

  // Afficher les détails d'une notification
  const handleViewNotification = (notification) => {
    setSelectedNotification(notification);
    setShowNotificationDialog(true);
  };

  // Formater le statut
  const formatStatus = (status) => {
    const statusConfig = {
      pending: { label: 'En attente', color: 'warning' },
      sent: { label: 'Envoyé', color: 'success' },
      failed: { label: 'Échoué', color: 'error' }
    };
    
    const config = statusConfig[status] || { label: status, color: 'default' };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  // Formater la date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Notifications Programmées
      </Typography>

      {/* Statistiques */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                En attente
              </Typography>
              <Typography variant="h4">
                {stats.pending || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Envoyées
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.sent || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Échouées
              </Typography>
              <Typography variant="h4" color="error.main">
                {stats.failed || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total
              </Typography>
              <Typography variant="h4">
                {stats.total || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Actions */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<PlayIcon />}
          onClick={handleGenerateNotifications}
          disabled={loading}
        >
          Générer les notifications
        </Button>
        
        <Button
          variant="contained"
          color="secondary"
          startIcon={<RefreshIcon />}
          onClick={handleProcessNotifications}
          disabled={loading}
        >
          Traiter les notifications
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<HistoryIcon />}
          onClick={() => {
            loadHistory();
            setShowHistoryDialog(true);
          }}
          disabled={loading}
        >
          Historique
        </Button>
        
        <Button
          variant="outlined"
          color="error"
          startIcon={<ErrorIcon />}
          onClick={() => {
            loadFailedNotifications();
            setShowFailedDialog(true);
          }}
          disabled={loading}
        >
          Notifications échouées ({stats.failed || 0})
        </Button>
        
        <Button
          variant="outlined"
          onClick={loadStats}
          disabled={loading}
        >
          Actualiser
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Dialog Historique */}
      <Dialog
        open={showHistoryDialog}
        onClose={() => setShowHistoryDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Historique des notifications
          <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Statut</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Statut"
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="pending">En attente</MenuItem>
                <MenuItem value="sent">Envoyé</MenuItem>
                <MenuItem value="failed">Échoué</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Template</InputLabel>
              <Select
                value={filterTemplate}
                onChange={(e) => setFilterTemplate(e.target.value)}
                label="Template"
              >
                <MenuItem value="">Tous</MenuItem>
                {templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Button
              variant="contained"
              size="small"
              onClick={() => loadHistory(1)}
            >
              Filtrer
            </Button>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Template</TableCell>
                  <TableCell>Destinataire</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Date de création</TableCell>
                  <TableCell>Date d'envoi</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((notification) => (
                  <TableRow key={notification.id}>
                    <TableCell>{notification.template_name}</TableCell>
                    <TableCell>
                      {notification.firstname} {notification.lastname}
                      <br />
                      <small>{notification.email}</small>
                    </TableCell>
                    <TableCell>{formatStatus(notification.status)}</TableCell>
                    <TableCell>{formatDate(notification.created_at)}</TableCell>
                    <TableCell>
                      {notification.sent_at ? formatDate(notification.sent_at) : '-'}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleViewNotification(notification)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(e, page) => {
                setCurrentPage(page);
                loadHistory(page);
              }}
            />
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setShowHistoryDialog(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Notifications échouées */}
      <Dialog
        open={showFailedDialog}
        onClose={() => setShowFailedDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Notifications échouées</DialogTitle>
        
        <DialogContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Template</TableCell>
                  <TableCell>Destinataire</TableCell>
                  <TableCell>Erreur</TableCell>
                  <TableCell>Tentatives</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {failedNotifications.map((notification) => (
                  <TableRow key={notification.id}>
                    <TableCell>{notification.template_name}</TableCell>
                    <TableCell>
                      {notification.firstname} {notification.lastname}
                      <br />
                      <small>{notification.email}</small>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="error">
                        {notification.error_message || 'Erreur inconnue'}
                      </Typography>
                    </TableCell>
                    <TableCell>{notification.attempts || 0}/3</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleRetryNotification(notification.id)}
                        disabled={notification.attempts >= 3}
                      >
                        Retenter
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setShowFailedDialog(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Détails notification */}
      <Dialog
        open={showNotificationDialog}
        onClose={() => setShowNotificationDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Détails de la notification</DialogTitle>
        
        <DialogContent>
          {selectedNotification && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Template: {selectedNotification.template_name}
              </Typography>
              
              <Typography variant="body1" gutterBottom>
                <strong>Destinataire:</strong> {selectedNotification.firstname} {selectedNotification.lastname}
              </Typography>
              
              <Typography variant="body1" gutterBottom>
                <strong>Email:</strong> {selectedNotification.email}
              </Typography>
              
              <Typography variant="body1" gutterBottom>
                <strong>Statut:</strong> {formatStatus(selectedNotification.status)}
              </Typography>
              
              <Typography variant="body1" gutterBottom>
                <strong>Date de création:</strong> {formatDate(selectedNotification.created_at)}
              </Typography>
              
              {selectedNotification.sent_at && (
                <Typography variant="body1" gutterBottom>
                  <strong>Date d'envoi:</strong> {formatDate(selectedNotification.sent_at)}
                </Typography>
              )}
              
              {selectedNotification.error_message && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <strong>Erreur:</strong> {selectedNotification.error_message}
                </Alert>
              )}
              
              {selectedNotification.metadata && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Métadonnées
                  </Typography>
                  <pre style={{ backgroundColor: '#f5f5f5', padding: '10px', borderRadius: '4px' }}>
                    {JSON.stringify(JSON.parse(selectedNotification.metadata), null, 2)}
                  </pre>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setShowNotificationDialog(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScheduledNotificationsPage; 